import React, { useContext, useEffect, useState, useCallback } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  Building2, 
  ClipboardList, 
  FileText, 
  Users, 
  FolderOpen,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  XCircle,
  CalendarDays,
  AlertTriangle,
  Award,
  BarChart2,
  Bell
} from "lucide-react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";
import { Link, useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  planned: "#3B82F6",
  in_progress: "#F59E0B", 
  completed: "#10B981",
  cancelled: "#6B7280",
  unknown: "#9CA3AF"  // Fallback for unknown status
};

const STATUS_LABELS = {
  planned: "Planlandı",
  in_progress: "Devam Ediyor",
  completed: "Tamamlandı",
  cancelled: "İptal",
  unknown: "Bilinmeyen"  // Fallback for unknown status
};

// Helper function to safely get status label
const getStatusLabel = (status) => {
  if (!status || status === 'unknown' || status === 'null' || status === 'undefined') {
    return STATUS_LABELS.planned;
  }
  return STATUS_LABELS[status] || status;
};

// Helper function to safely get status color
const getStatusColor = (status) => {
  if (!status || status === 'unknown' || status === 'null' || status === 'undefined') {
    return COLORS.planned;
  }
  return COLORS[status] || '#8884d8';
};

const StatCard = ({ title, value, icon: Icon, color, subtext, loading }) => (
  <Card className="bg-white border-[#E8F3EE] hover:shadow-lg transition-all duration-300 group">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-[#4B5563] mb-1">{title}</p>
          <p className="text-3xl font-bold text-[#111827]">{loading ? "-" : value || 0}</p>
          {subtext && <p className="text-xs text-[#9CA3AF] mt-2">{subtext}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [monthlyData, setMonthlyData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [stageData, setStageData] = useState([]);
  const [certificateAlerts, setCertificateAlerts] = useState({ expired: [], expiring_soon: [] });
  const [auditorPerformance, setAuditorPerformance] = useState([]);
  const [overdueAudits, setOverdueAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Helper to get date in Turkey timezone as YYYY-MM-DD
  const getTurkeyDateString = (date = new Date()) => {
    const options = { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' };
    return new Intl.DateTimeFormat('sv-SE', options).format(date);
  };
  
  // Chart date range - Default to 2026 full year
  const [chartStartDate, setChartStartDate] = useState("2026-01-01");
  const [chartEndDate, setChartEndDate] = useState("2026-12-31");

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats/dashboard`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error("Stats error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchChartData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (chartStartDate) params.append('start_date', chartStartDate);
      if (chartEndDate) params.append('end_date', chartEndDate);
      
      const [monthlyRes, statusRes, stageRes] = await Promise.all([
        axios.get(`${API}/stats/monthly-audits?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/stats/audit-status?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/stats/audit-stage-distribution?${params.toString()}`, { withCredentials: true })
      ]);
      setMonthlyData(monthlyRes.data);
      setStatusData(statusRes.data);
      setStageData(stageRes.data);
    } catch (error) {
      console.error("Chart data error:", error);
    }
  }, [chartStartDate, chartEndDate]);

  const fetchCertificateAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats/certificate-alerts`, { withCredentials: true });
      setCertificateAlerts(response.data);
    } catch (error) {
      console.error("Certificate alerts error:", error);
    }
  }, []);

  const fetchAuditorPerformance = useCallback(async () => {
    if (user?.role !== "admin" && user?.role !== "super_admin") return;
    try {
      const response = await axios.get(`${API}/stats/auditor-performance`, { withCredentials: true });
      setAuditorPerformance(response.data);
    } catch (error) {
      console.error("Auditor performance error:", error);
    }
  }, [user?.role]);

  const fetchOverdueAudits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats/overdue-audits`, { withCredentials: true });
      setOverdueAudits(response.data);
    } catch (error) {
      console.error("Overdue audits error:", error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchChartData();
    fetchCertificateAlerts();
    fetchAuditorPerformance();
    fetchOverdueAudits();
  }, [fetchStats, fetchChartData, fetchCertificateAlerts, fetchAuditorPerformance, fetchOverdueAudits]);

  const getRoleLabel = (role) => {
    const labels = {
      super_admin: "Süper Admin",
      admin: "Admin",
      technical_manager: "Teknik Yönetici",
      planning_expert: "Planlama Uzmanı",
      auditor: "Denetçi"
    };
    return labels[role] || role;
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-[#1A4D2E] to-[#2D6A4F] rounded-2xl p-8 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">
              Hoş geldiniz, {user?.name?.split(" ")[0]}! 👋
            </h1>
            <p className="text-white/80">
              {getRoleLabel(user?.role)} olarak giriş yaptınız
            </p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg backdrop-blur-sm">
            <TrendingUp className="w-5 h-5" />
            <span className="text-sm">Sistem aktif</span>
          </div>
        </div>
      </div>

      {/* Overdue Reports Alert - For Auditors and Admin */}
      {overdueAudits.length > 0 && (
        <Card className="border-red-200 bg-red-50" data-testid="overdue-reports-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-red-800 flex items-center gap-2">
              <Bell className="w-5 h-5 text-red-600 animate-pulse" />
              Gecikmiş Raporlar ({overdueAudits.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-700 mb-4">
              Aşağıdaki denetimler için henüz rapor girilmedi. Raporlar denetim tarihinden itibaren 15 gün içinde girilmelidir.
            </p>
            <div className="space-y-3">
              {overdueAudits.slice(0, 5).map((audit) => (
                <div 
                  key={audit.audit_id} 
                  className="flex items-center justify-between bg-white rounded-lg p-3 border border-red-200"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{audit.customer_name}</p>
                    <p className="text-sm text-gray-500">{audit.title}</p>
                    {(user?.role === "admin" || user?.role === "super_admin") && audit.auditor_name && (
                      <p className="text-xs text-gray-400">Denetçi: {audit.auditor_name}</p>
                    )}
                  </div>
                  <div className="text-right mr-4">
                    <p className="text-sm text-gray-500">
                      {new Date(audit.end_date).toLocaleDateString('tr-TR')}
                    </p>
                    <p className={`text-sm font-bold ${audit.days_overdue > 20 ? 'text-red-600' : 'text-orange-600'}`}>
                      {audit.days_overdue} gün gecikmiş
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/audits/${audit.audit_id}`)}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Rapor Oluştur
                  </Button>
                </div>
              ))}
              {overdueAudits.length > 5 && (
                <p className="text-center text-sm text-red-600">
                  ve {overdueAudits.length - 5} denetim daha...
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Certificate Alerts Section */}
      {(user?.role === "admin" || user?.role === "super_admin" || user?.role === "planning_expert") && (certificateAlerts.expired.length > 0 || certificateAlerts.expiring_soon.length > 0) && (
        <Card className="border-[#E8F3EE] border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#111827] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Belge Geçerlilik Uyarıları
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Expired Certificates */}
              {certificateAlerts.expired.length > 0 && (
                <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                  <div className="flex items-center gap-2 mb-3">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <h3 className="font-semibold text-red-700">Süresi Dolan Belgeler ({certificateAlerts.expired.length})</h3>
                  </div>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {certificateAlerts.expired.map((customer) => (
                      <Link 
                        key={customer.customer_id}
                        to="/customers"
                        className="flex items-center justify-between p-2 rounded bg-white hover:bg-red-100 transition-colors"
                      >
                        <div>
                          <p className="font-medium text-[#111827] text-sm">{customer.name}</p>
                          <p className="text-xs text-red-600">
                            {new Date(customer.certificate_validity_date).toLocaleDateString('tr-TR')} ({Math.abs(customer.days_remaining)} gün önce)
                          </p>
                        </div>
                        <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">
                          {customer.audit_phase}. Aşama
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Expiring Soon Certificates */}
              {certificateAlerts.expiring_soon.length > 0 && (
                <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-yellow-500" />
                    <h3 className="font-semibold text-yellow-700">Yaklaşan Son Tarihler ({certificateAlerts.expiring_soon.length})</h3>
                  </div>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {certificateAlerts.expiring_soon.map((customer) => (
                      <Link 
                        key={customer.customer_id}
                        to="/customers"
                        className="flex items-center justify-between p-2 rounded bg-white hover:bg-yellow-100 transition-colors"
                      >
                        <div>
                          <p className="font-medium text-[#111827] text-sm">{customer.name}</p>
                          <p className="text-xs text-yellow-600">
                            {new Date(customer.certificate_validity_date).toLocaleDateString('tr-TR')} ({customer.days_remaining} gün kaldı)
                          </p>
                        </div>
                        <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">
                          {customer.audit_phase}. Aşama
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin/Super Admin Stats - Consolidated View */}
      {(user?.role === "admin" || user?.role === "super_admin") && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard loading={loading} 
            title="Toplam Müşteri" 
            value={stats.total_customers} 
            icon={Building2}
            color="bg-[#1A4D2E]"
          />
          <StatCard loading={loading} 
            title="Toplam Denetim" 
            value={stats.total_audits} 
            icon={ClipboardList}
            color="bg-[#2D6A4F]"
          />
          <StatCard loading={loading} 
            title="Toplam Rapor" 
            value={stats.total_reports} 
            icon={FileText}
            color="bg-[#D97706]"
          />
          <StatCard loading={loading} 
            title="Kullanıcı Sayısı" 
            value={stats.total_users} 
            icon={Users}
            color="bg-[#4B5563]"
          />
        </div>
      )}

      {/* Technical Manager Stats - Only show if NOT admin/super_admin */}
      {user?.role === "technical_manager" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard loading={loading} 
            title="Toplam Rapor" 
            value={stats.total_reports} 
            icon={FileText}
            color="bg-[#1A4D2E]"
          />
          <StatCard loading={loading} 
            title="Onay Bekleyen" 
            value={stats.pending_reports} 
            icon={AlertCircle}
            color="bg-[#D97706]"
            subtext="İncelemeniz gereken raporlar"
          />
          <StatCard loading={loading} 
            title="Onaylanan Rapor" 
            value={stats.approved_reports} 
            icon={CheckCircle}
            color="bg-green-500"
          />
        </div>
      )}

      {/* Planning Expert Stats - Only show if NOT admin/super_admin */}
      {user?.role === "planning_expert" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard loading={loading} 
            title="Toplam Müşteri" 
            value={stats.total_customers} 
            icon={Building2}
            color="bg-[#1A4D2E]"
          />
          <StatCard loading={loading} 
            title="Toplam Denetim" 
            value={stats.total_audits} 
            icon={ClipboardList}
            color="bg-[#2D6A4F]"
          />
          <StatCard loading={loading} 
            title="Planlanan Denetim" 
            value={stats.planned_audits} 
            icon={Clock}
            color="bg-blue-500"
          />
          <StatCard loading={loading} 
            title="Tamamlanan Denetim" 
            value={stats.completed_audits} 
            icon={CheckCircle}
            color="bg-green-500"
          />
        </div>
      )}

      {/* Auditor Stats - Only show if auditor role */}
      {user?.role === "auditor" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard loading={loading} 
            title="Atanan Denetimler" 
            value={stats.my_audits} 
            icon={ClipboardList}
            color="bg-[#1A4D2E]"
          />
          <StatCard loading={loading} 
            title="Bekleyen Denetim" 
            value={stats.my_planned_audits} 
            icon={Clock}
            color="bg-blue-500"
            subtext="Planlanmış denetimleriniz"
          />
          <StatCard loading={loading} 
            title="Raporlarım" 
            value={stats.my_reports} 
            icon={FileText}
            color="bg-[#D97706]"
          />
          <StatCard loading={loading} 
            title="Taslak Raporlar" 
            value={stats.my_draft_reports} 
            icon={AlertCircle}
            color="bg-yellow-500"
            subtext="Gönderilmemiş raporlar"
          />
        </div>
      )}

      {/* Quick Actions */}
      <Card className="border-[#E8F3EE]">
        <CardHeader>
          <CardTitle className="text-lg text-[#111827]">Hızlı İşlemler</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(user?.role === "admin" || user?.role === "super_admin" || user?.role === "planning_expert") && (
              <>
                <a 
                  href="/customers" 
                  className="p-4 rounded-xl bg-[#E8F3EE] hover:bg-[#1A4D2E] hover:text-white text-[#1A4D2E] transition-all group"
                  data-testid="quick-customers"
                >
                  <Building2 className="w-6 h-6 mb-2" />
                  <span className="text-sm font-medium">Müşteri Ekle</span>
                </a>
                <a 
                  href="/audits" 
                  className="p-4 rounded-xl bg-[#E8F3EE] hover:bg-[#1A4D2E] hover:text-white text-[#1A4D2E] transition-all group"
                  data-testid="quick-audits"
                >
                  <ClipboardList className="w-6 h-6 mb-2" />
                  <span className="text-sm font-medium">Denetim Planla</span>
                </a>
              </>
            )}
            <a 
              href="/calendar" 
              className="p-4 rounded-xl bg-[#E8F3EE] hover:bg-[#1A4D2E] hover:text-white text-[#1A4D2E] transition-all group"
              data-testid="quick-calendar"
            >
              <Clock className="w-6 h-6 mb-2" />
              <span className="text-sm font-medium">Takvim</span>
            </a>
            <a 
              href="/reports" 
              className="p-4 rounded-xl bg-[#E8F3EE] hover:bg-[#1A4D2E] hover:text-white text-[#1A4D2E] transition-all group"
              data-testid="quick-reports"
            >
              <FileText className="w-6 h-6 mb-2" />
              <span className="text-sm font-medium">Raporlar</span>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Charts Section - Only for Admin, Super Admin, Technical Manager, Planning Expert */}
      {(user?.role === "admin" || user?.role === "super_admin" || user?.role === "technical_manager" || user?.role === "planning_expert") && (
        <>
          {/* Chart Date Range Filter */}
          <Card className="border-[#E8F3EE]">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <span className="text-sm font-medium text-gray-700">Grafik Tarih Aralığı:</span>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={chartStartDate}
                    onChange={(e) => setChartStartDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="date"
                    value={chartEndDate}
                    onChange={(e) => setChartEndDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const date = new Date();
                      date.setMonth(date.getMonth() - 3);
                      setChartStartDate(getTurkeyDateString(date));
                      setChartEndDate(getTurkeyDateString());
                    }}
                  >
                    Son 3 Ay
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const date = new Date();
                      date.setMonth(date.getMonth() - 6);
                      setChartStartDate(getTurkeyDateString(date));
                      setChartEndDate(getTurkeyDateString());
                    }}
                  >
                    Son 6 Ay
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const date = new Date();
                      date.setFullYear(date.getFullYear() - 1);
                      setChartStartDate(getTurkeyDateString(date));
                      setChartEndDate(getTurkeyDateString());
                    }}
                  >
                    Son 1 Yıl
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Audits Chart - Full Width at Top */}
          <Card className="border-[#E8F3EE]">
            <CardHeader>
              <CardTitle className="text-lg text-[#111827]">Aylık Denetim Sayıları</CardTitle>
            </CardHeader>
            <CardContent>
              {monthlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8F3EE" />
                    <XAxis dataKey="month" tick={{ fill: '#4B5563', fontSize: 12 }} />
                    <YAxis 
                      tick={{ fill: '#4B5563', fontSize: 12 }} 
                      allowDecimals={false}
                      tickFormatter={(value) => Math.floor(value)}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E8F3EE',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill="#1A4D2E" radius={[4, 4, 0, 0]} name="Denetim" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-[#9CA3AF]">
                  Henüz veri yok
                </div>
              )}
            </CardContent>
          </Card>

          {/* Two Charts Side by Side Below */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Distribution Chart */}
            <Card className="border-[#E8F3EE]">
              <CardHeader>
                <CardTitle className="text-lg text-[#111827]">Denetim Durumu Dağılımı</CardTitle>
              </CardHeader>
              <CardContent>
                {statusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="count"
                        nameKey="status"
                        label={({ status, percent }) => `${getStatusLabel(status)} ${(percent * 100).toFixed(0)}%`}
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getStatusColor(entry.status)} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value, name) => [value, getStatusLabel(name)]}
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #E8F3EE',
                          borderRadius: '8px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-[#9CA3AF]">
                    Henüz veri yok
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stage Distribution Chart */}
            <Card className="border-[#E8F3EE]">
              <CardHeader>
                <CardTitle className="text-lg text-[#111827]">Aşama Dağılımı</CardTitle>
              </CardHeader>
              <CardContent>
                {stageData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={stageData.map(item => ({
                          ...item,
                          displayLabel: item.stage === 'tr_i' || item.stage === 'stage2' || item.label === 'tr_i' 
                            ? '2. Aşama (TR-I)' 
                            : item.stage === 'gstc' || item.stage === 'stage3' || item.label === 'gstc'
                            ? '3. Aşama (GSTC)'
                            : item.label || item.stage
                        }))}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="count"
                        nameKey="displayLabel"
                        label={({ displayLabel, percent }) => `${displayLabel} ${(percent * 100).toFixed(0)}%`}
                      >
                        {stageData.map((entry, index) => {
                          const stageColors = {
                            'tr_i': '#F97316',      // Orange for Stage 2
                            'stage2': '#F97316',
                            'gstc': '#16A34A',      // Green for Stage 3
                            'stage3': '#16A34A',
                            'unknown': '#9CA3AF'
                          };
                          const color = stageColors[entry.stage] || stageColors[entry.label] || '#8B5CF6';
                          return <Cell key={`cell-${index}`} fill={color} />;
                        })}
                      </Pie>
                      <Tooltip 
                        formatter={(value, name) => [value, name]}
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #E8F3EE',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend 
                        formatter={(value) => {
                          if (value === 'tr_i' || value === 'stage2') return '2. Aşama (TR-I)';
                          if (value === 'gstc' || value === 'stage3') return '3. Aşama (GSTC)';
                          return value;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-[#9CA3AF]">
                    Henüz veri yok
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Status Summary Cards */}
      {(user?.role === "admin" || user?.role === "super_admin" || user?.role === "planning_expert") && (
        <Card className="border-[#E8F3EE]">
          <CardHeader>
            <CardTitle className="text-lg text-[#111827]">Durum Özeti</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold text-blue-700">{stats.planned_audits || 0}</p>
                    <p className="text-sm text-blue-600">Planlandı</p>
                  </div>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-yellow-50 border border-yellow-200">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-8 h-8 text-yellow-500" />
                  <div>
                    <p className="text-2xl font-bold text-yellow-700">{stats.in_progress_audits || 0}</p>
                    <p className="text-sm text-yellow-600">Devam Ediyor</p>
                  </div>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-green-50 border border-green-200">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold text-green-700">{stats.completed_audits || 0}</p>
                    <p className="text-sm text-green-600">Tamamlandı</p>
                  </div>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-3">
                  <XCircle className="w-8 h-8 text-gray-500" />
                  <div>
                    <p className="text-2xl font-bold text-gray-700">{stats.cancelled_audits || 0}</p>
                    <p className="text-sm text-gray-600">İptal</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Auditor Performance Stats - Admin Only */}
      {(user?.role === "admin" || user?.role === "super_admin") && auditorPerformance.length > 0 && (
        <Card className="border-[#E8F3EE]" data-testid="auditor-performance-card">
          <CardHeader>
            <CardTitle className="text-lg text-[#111827] flex items-center gap-2">
              <Award className="w-5 h-5 text-[#1A4D2E]" />
              Denetçi Performans İstatistikleri
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E8F3EE]">
                    <th className="text-left py-3 px-4 font-semibold text-[#4B5563]">Denetçi</th>
                    <th className="text-center py-3 px-2 font-semibold text-[#4B5563]">Toplam</th>
                    <th className="text-center py-3 px-2 font-semibold text-[#4B5563]">Tamamlanan</th>
                    <th className="text-center py-3 px-2 font-semibold text-[#4B5563]">Devam Eden</th>
                    <th className="text-center py-3 px-2 font-semibold text-[#4B5563]">Raporlar</th>
                    <th className="text-center py-3 px-2 font-semibold text-[#4B5563]">Onaylı</th>
                    <th className="text-center py-3 px-2 font-semibold text-[#4B5563]">Ort. Süre</th>
                    <th className="text-center py-3 px-2 font-semibold text-[#4B5563]">Tamamlama</th>
                  </tr>
                </thead>
                <tbody>
                  {auditorPerformance.map((auditor, idx) => (
                    <tr key={auditor.auditor_id} className={idx % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"}>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-[#111827]">{auditor.name}</p>
                          <p className="text-xs text-[#9CA3AF]">{auditor.email}</p>
                        </div>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-semibold">
                          {auditor.total_audits}
                        </span>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700 font-semibold">
                          {auditor.completed_audits}
                        </span>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 text-yellow-700 font-semibold">
                          {auditor.in_progress_audits}
                        </span>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className="font-medium text-[#4B5563]">{auditor.total_reports}</span>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className="font-medium text-green-600">{auditor.approved_reports}</span>
                      </td>
                      <td className="text-center py-3 px-2">
                        {auditor.avg_duration_days ? (
                          <span className="text-[#4B5563]">{auditor.avg_duration_days} gün</span>
                        ) : (
                          <span className="text-[#9CA3AF]">-</span>
                        )}
                      </td>
                      <td className="text-center py-3 px-2">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#1A4D2E] rounded-full transition-all"
                              style={{ width: `${auditor.completion_rate}%` }}
                            />
                          </div>
                          <span className="text-xs text-[#4B5563] w-10">%{auditor.completion_rate}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
